import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Truck, ShieldCheck } from "lucide-react";
import { adminClient, setToken, getToken, formatApiErrorDetail } from "./api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await adminClient.post("/auth/admin/login", { email, password });
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.user.name || "Harvey"}`);
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C2E2B] grain-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 border-2 border-[#0F3B37] bg-white">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#FFDF00] grid place-items-center">
            <Truck className="w-5 h-5 text-[#0C2E2B]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-lg text-[#0C2E2B]">HARVEY'S PLACE</div>
            <div className="font-tactical text-[10px] uppercase tracking-[0.25em] text-zinc-500">Admin Console</div>
          </div>
        </div>

        <h1 className="font-display font-black text-2xl text-[#0C2E2B]">Sign in.</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage merch, marketplace, bookings.</p>

        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="admin-login-form">
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-zinc-700">Email</Label>
            <Input
              id="email"
              data-testid="admin-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="harvey@harveysplace.co"
              className="mt-1 rounded-md"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-zinc-700">Password</Label>
            <Input
              id="password"
              data-testid="admin-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 rounded-md"
            />
          </div>
          <Button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={busy}
            className="btn-kinetic rounded-md w-full py-6 text-sm"
          >
            {busy ? "Signing in..." : "Sign In"}
          </Button>
          <div className="flex items-center gap-2 text-[11px] text-zinc-500 pt-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Sessions expire after 12 hours.
          </div>
        </form>
      </Card>
    </div>
  );
}
